import PropTypes from "prop-types";
import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import "../../assets/styles/colors.css";

function filterBySearch(nodeTypes, query) {
  const q = (query || "").trim().toLowerCase();
  if (!q) return nodeTypes;
  return nodeTypes.filter(
    (nt) =>
      nt.label?.toLowerCase().includes(q) ||
      nt.type?.toLowerCase().includes(q) ||
      nt.category?.toLowerCase().includes(q),
  );
}

function DeviceItemMinimal({
  nodeType,
  onDragStart,
  onDeviceClick,
  clickMode,
}) {
  const handleClick = () => {
    if (onDeviceClick) onDeviceClick(nodeType.type);
  };

  return (
    <div
      className={`flex items-center gap-3 rounded-lg border border-(--color-primary)/25 bg-transparent px-3 py-3 text-sm text-(--color-primary) transition-colors duration-200 hover:-translate-y-0.5 hover:border-(--color-primary) hover:bg-(--color-primary)/10 select-none ${clickMode ? "cursor-pointer active:scale-95" : "cursor-grab active:cursor-grabbing"}`}
      draggable={!clickMode}
      onDragStart={(e) => !clickMode && onDragStart(e, nodeType.type)}
      onClick={() => clickMode && handleClick()}
    >
      <span className="flex h-7 w-7 items-center justify-center rounded-md border border-(--color-primary)/30 bg-(--color-card)">
        <img
          src={nodeType.icon}
          alt={nodeType.label}
          className="h-3.5 w-3.5 object-contain"
        />
      </span>
      <span className="text-(--color-text-white)">{nodeType.label}</span>
      {clickMode && (
        <span className="ml-auto text-xs text-gray-400">tap to add</span>
      )}
    </div>
  );
}

function DeviceLibraryFull({
  nodeTypes,
  categories = [],
  onDragStart,
  onDeviceClick,
  clickMode,
  title,
  loading = false,
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState("grid");

  const filtered = useMemo(
    () => filterBySearch(nodeTypes, search),
    [nodeTypes, search],
  );

  const categoryOrder = useMemo(() => {
    const fromApi = Array.isArray(categories) ? categories : [];
    const fromDevices = [
      ...new Set(filtered.map((nt) => nt.category).filter(Boolean)),
    ];
    const extra = fromDevices.filter((c) => !fromApi.includes(c));
    return [...fromApi, ...extra];
  }, [categories, filtered]);

  const byCategory = useMemo(() => {
    const map = {};
    categoryOrder.forEach((cat) => {
      map[cat] = filtered.filter((nt) => nt.category === cat);
    });
    return map;
  }, [filtered, categoryOrder]);

  const itemProps = { onDragStart, onDeviceClick, clickMode };

  if (loading) {
    return (
      <div className="flex flex-col h-full min-h-0 overflow-hidden bg-(--color-card)">
        <div className="p-4 border-b border-(--color-border) bg-transparent flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            <span className="font-bold text-sm uppercase tracking-wider text-black dark:text-(--color-text-white)">
              {title}
            </span>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center p-4 text-sm text-(--color-text-muted)">
          {t("Loading devices...", { defaultValue: "Loading devices..." })}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden bg-(--color-card)">
      <div className="p-4 border-b border-(--color-border) bg-transparent flex flex-wrap items-center justify-between gap-2 shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <svg
            className="w-5 h-5 text-(--color-primary)"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
            />
          </svg>
          <h2 className="font-bold text-sm uppercase tracking-wider text-black dark:text-(--color-text-white)">
            {title || t("Device Library")}
          </h2>
        </div>
        <div data-tour="device-palette-view-toggle" className="flex items-center gap-1 rounded-lg border border-slate-200 dark:border-(--color-border) bg-white dark:bg-(--color-bg-card) p-1 shadow-sm shrink-0">
          <button
            type="button"
            onClick={() => setViewMode("grid")}
            className={`w-6 h-6 sm:w-7 sm:h-7 rounded-md flex items-center justify-center transition-colors ${
              viewMode === "grid"
                ? "bg-(--color-primary)/10 text-(--color-primary)"
                : "text-slate-500 hover:text-(--color-primary) hover:bg-slate-100 dark:hover:bg-(--color-bg-overlay)"
            }`}
            title={t("Grid view")}
          >
            <svg
              className="w-3.5 h-3.5 sm:w-4 sm:h-4"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M4 4h7v7H4V4zm9 0h7v7h-7V4zM4 13h7v7H4v-7zm9 0h7v7h-7v-7z" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => setViewMode("list")}
            className={`w-6 h-6 sm:w-7 sm:h-7 rounded-md flex items-center justify-center transition-colors ${
              viewMode === "list"
                ? "bg-(--color-primary)/10 text-(--color-primary)"
                : "text-slate-500 hover:text-(--color-primary) hover:bg-slate-100 dark:hover:bg-(--color-bg-overlay)"
            }`}
            title={t("List view")}
          >
            <svg
              className="w-3.5 h-3.5 sm:w-4 sm:h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8 6h13M3 6h.01M8 12h13M3 12h.01M8 18h13M3 18h.01"
              />
            </svg>
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 p-4 overflow-y-auto space-y-6 topology-devices-scrollbar">
        <div className="relative" data-tour="device-palette-search">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("topology.device_library.search_placeholder")}
            className="w-full pl-9 pr-3 py-2 bg-transparent dark:bg-transparent border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-900 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-(--color-primary)/30 focus:border-(--color-primary) transition-all"
          />
          <svg
            className="w-4 h-4 text-slate-400 dark:text-slate-500 absolute left-3 top-1/2 -translate-y-1/2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>

        {viewMode === "list" ? (
          <div className="space-y-2">
            {filtered.map((nodeType) => (
              <div
                key={nodeType.type}
                draggable={!clickMode}
                onDragStart={(e) => !clickMode && onDragStart(e, nodeType.type)}
                onClick={() => clickMode && onDeviceClick?.(nodeType.type)}
                className={`group flex items-center gap-3 px-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg hover:border-green-500 hover:bg-green-50 dark:hover:bg-green-950/30 transition-all bg-white dark:bg-transparent ${clickMode ? "cursor-pointer" : "cursor-grab active:cursor-grabbing"}`}
              >
                <div className="w-8 h-8 flex items-center justify-center grayscale group-hover:grayscale-0 transition-all">
                  <img
                    src={nodeType.icon}
                    alt={nodeType.label}
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="flex-1">
                  <div className="text-xs font-semibold text-slate-700 dark:text-slate-300 group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors">
                    {nodeType.label}
                  </div>
                  <div className="text-[10px] text-slate-400 dark:text-slate-500">
                    {nodeType.category}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            {categoryOrder.map((category) => {
              const items = byCategory[category] || [];
              if (items.length === 0) return null;
              return (
                <div key={category}>
                  <h3 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-3">
                    {t(category)}
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    {items.map((nodeType, nodeIdx) => (
                      <div
                        key={nodeType.type}
                        draggable={!clickMode}
                        onDragStart={(e) =>
                          !clickMode && onDragStart(e, nodeType.type)
                        }
                        onClick={() =>
                          clickMode && onDeviceClick?.(nodeType.type)
                        }
                        {...(nodeIdx === 0 && { "data-tour": "device-palette-item" })} className={`group p-2 border border-slate-200 dark:border-slate-700 rounded-lg flex flex-col items-center gap-1.5 hover:border-green-500 hover:bg-green-50 dark:hover:bg-green-950/30 transition-all bg-white dark:bg-transparent ${clickMode ? "cursor-pointer" : "cursor-grab active:cursor-grabbing"}`}
                      >
                        <div className="w-8 h-8 flex items-center justify-center transition-all grayscale group-hover:grayscale-0 group-hover:scale-110">
                          <img
                            src={nodeType.icon}
                            alt={nodeType.label}
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <span className="text-[10px] font-medium text-slate-600 dark:text-slate-400 group-hover:text-green-600 dark:group-hover:text-green-400 text-center leading-tight">
                          {nodeType.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}

function DevicesPaletteContent({
  nodeTypes = [],
  categories = [],
  onDragStart,
  onDeviceClick,
  clickMode = false,
  variant = "minimal",
  title,
  loading = false,
}) {
  if (variant === "full") {
    return (
      <DeviceLibraryFull
        nodeTypes={nodeTypes}
        categories={categories}
        onDragStart={onDragStart}
        onDeviceClick={onDeviceClick}
        clickMode={clickMode}
        title={title}
        loading={loading}
      />
    );
  }

  return (
    <div className="flex flex-col gap-2 p-3 overflow-y-auto h-full w-full topology-devices-scrollbar">
      {nodeTypes.map((nodeType) => (
        <DeviceItemMinimal
          key={nodeType.type}
          nodeType={nodeType}
          onDragStart={onDragStart}
          onDeviceClick={onDeviceClick}
          clickMode={clickMode}
        />
      ))}
    </div>
  );
}

DevicesPaletteContent.propTypes = {
  nodeTypes: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.string,
      category: PropTypes.string,
    }),
  ).isRequired,
  categories: PropTypes.arrayOf(PropTypes.string),
  onDragStart: PropTypes.func,
  onDeviceClick: PropTypes.func,
  clickMode: PropTypes.bool,
  variant: PropTypes.oneOf(["minimal", "full"]),
  title: PropTypes.string,
  loading: PropTypes.bool,
};

export default DevicesPaletteContent;
